import { useEffect, useState } from "react";
import { LogOut, Save, Eye } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo, { CitekMark } from "@/components/Logo";
import FieldEditor, { inputCls } from "./admin/FieldEditor";
import MessagesList from "./admin/MessagesList";

const SECTION_LABELS = ["hero", "marquee", "stats", "services", "projects", "about", "trust", "process", "rse", "contact", "footer"];

function ContentEditor({ content, setContent }) {
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Modifications enregistrées. Le site est à jour.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-content-editor">
      {SECTION_LABELS.filter((k) => content[k] !== undefined).map((key, i) => (
        <details key={key} open={i === 0} className="border border-black/10 rounded-lg bg-white" data-testid={`admin-section-${key}`}>
          <summary className="cursor-pointer px-5 py-4 font-tech text-sm uppercase tracking-[0.2em] text-citek-greenDark">
            {key === "hero" ? "Hero (accueil)" : key === "marquee" ? "Bandeau défilant" : key === "stats" ? "Chiffres clés" : key === "services" ? "Métiers (grille)" : key === "projects" ? "Réalisations" : key === "about" ? "L'entreprise" : key === "trust" ? "Ils nous font confiance" : key === "process" ? "Méthode" : key === "rse" ? "Engagements RSE" : key === "contact" ? "Contact & devis" : "Pied de page"}
          </summary>
          <div className="px-5 pb-5">
            <FieldEditor labelKey={key} value={content[key]} path={key} onChange={(v) => setContent({ ...content, [key]: v })} />
          </div>
        </details>
      ))}
      <button
        onClick={save}
        disabled={saving}
        data-testid="admin-save-button"
        className="w-full inline-flex items-center justify-center gap-2 bg-citek-green text-black font-semibold text-base px-6 py-4 rounded-md hover:bg-citek-greenBright transition-colors disabled:opacity-60"
      >
        <Save size={17} />
        {saving ? "Enregistrement…" : "Enregistrer les modifications"}
      </button>
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/login", { email, password });
      const { data } = await api.get("/auth/me");
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#EBECE8] flex items-center justify-center px-5" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>
        <form onSubmit={submit} data-testid="admin-login-form" className="border border-black/10 rounded-xl bg-white p-7 space-y-4 shadow-sm">
          <p className="font-tech text-xs uppercase tracking-[0.22em] text-citek-greenDark text-center">Espace administrateur</p>
          <div>
            <label className="block font-tech text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5" htmlFor="a-email">Email</label>
            <input id="a-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className={inputCls} placeholder="admin@citek-entreprise.fr" />
          </div>
          <div>
            <label className="block font-tech text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5" htmlFor="a-password">Mot de passe</label>
            <input id="a-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className={inputCls} placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-red-500" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-button" className="w-full bg-citek-green text-black font-semibold text-base px-6 py-3.5 rounded-md hover:bg-citek-greenBright transition-colors disabled:opacity-60">
            {loading ? "Connexion…" : "Se connecter"}
          </button>
          <a href="/" className="block text-center font-tech text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-citek-greenDark transition-colors" data-testid="admin-back-to-site">
            ← Retour au site
          </a>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("content");
  const [content, setContent] = useState(null);
  const [messages, setMessages] = useState([]);

  const loadMessages = () => api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/content").then((r) => setContent(r.data)).catch(() => setContent(null));
      loadMessages();
    }
  }, [user]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#EBECE8] flex items-center justify-center" data-testid="admin-loading">
        <CitekMark size={48} className="animate-pulse" />
      </div>
    );
  }

  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-[#EBECE8] text-zinc-900" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-black/10 bg-white/90 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-5 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo compact />
            <span className="hidden sm:inline font-tech text-[11px] uppercase tracking-[0.22em] text-citek-greenDark border border-citek-greenDark/30 rounded px-2 py-1" data-testid="admin-live-badge">
              Console éditeur
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" data-testid="admin-view-site-button" className="inline-flex items-center gap-2 border border-black/15 rounded-md px-3.5 py-2.5 text-sm text-zinc-600 hover:border-citek-greenDark hover:text-citek-greenDark transition-colors">
              <Eye size={15} /> Voir le site
            </a>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-black/15 rounded-md px-3.5 py-2.5 text-sm text-zinc-600 hover:border-red-400 hover:text-red-500 transition-colors">
              <LogOut size={15} /> Quitter
            </button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-5 flex gap-1 -mb-px">
          <button onClick={() => setTab("content")} data-testid="admin-tab-content" className={`px-4 py-2.5 font-tech text-xs uppercase tracking-[0.18em] border-b-2 transition-colors ${tab === "content" ? "border-citek-greenDark text-citek-greenDark" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
            Contenu du site
          </button>
          <button onClick={() => setTab("messages")} data-testid="admin-tab-messages" className={`px-4 py-2.5 font-tech text-xs uppercase tracking-[0.18em] border-b-2 transition-colors ${tab === "messages" ? "border-citek-greenDark text-citek-greenDark" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
            Demandes de devis {messages.length ? `(${messages.length})` : ""}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8">
        {tab === "content" && (content ? <ContentEditor content={content} setContent={setContent} /> : <p className="text-base text-zinc-500">Chargement du contenu…</p>)}
        {tab === "messages" && <MessagesList messages={messages} refresh={loadMessages} />}
      </main>
    </div>
  );
}
